import {
  useCreatePromoBanner,
  useUpdatePromoBanner,
} from '@/api/promo-banner/promo-banner.hooks';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useZodForm,
} from '@/lib/form';
import {
  promoBannerSchema,
  updatePromoBannerSchema,
  UpdatePromoBannerSchemaType,
} from '@/schemas/promo-banner.schema';
import { PromoBannerResponse } from '@repo/types/src/promo-banner';
import { CirclePlus, Pencil } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { TextPartsEditor } from './TextPartsEditor';

interface Props {
  banner?: PromoBannerResponse;
}

export const AddPromoBanner = ({ banner }: Props = {}) => {
  const isEdit = banner !== undefined;
  const createMutation = useCreatePromoBanner();
  const updateMutation = useUpdatePromoBanner();
  const [open, setOpen] = useState(false);

  const form = useZodForm({
    schema: isEdit ? updatePromoBannerSchema : promoBannerSchema,
    defaultValues: {
      region: banner?.region ?? 'FR',
      textParts: banner?.textParts ?? [{ text: '', highlight: false }],
      buttonText: banner?.buttonText ?? '',
      buttonLink: banner?.buttonLink ?? '',
    },
  });

  const handleSubmit = (payload: UpdatePromoBannerSchemaType) => {
    if (isEdit) {
      updateMutation.mutate(
        { id: banner.id, payload },
        {
          onSuccess: () => {
            setOpen(false);
            toast.success('Bannière modifiée avec succès !', {
              duration: 3000,
            });
          },
          onError: () =>
            toast.error('Erreur lors de la modification', { duration: 3000 }),
        },
      );
    } else {
      if (!payload.file) {
        toast.error('Veuillez ajouter une image', { duration: 3000 });
        return;
      }
      createMutation.mutate(
        { ...payload, file: payload.file },
        {
          onSuccess: () => {
            setOpen(false);
            toast.success('Bannière créée avec succès !', { duration: 3000 });
          },
          onError: () =>
            toast.error('Erreur lors de la création', { duration: 3000 }),
        },
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isEdit ? (
          <Button variant="ghost" size="icon">
            <Pencil size={20} />
          </Button>
        ) : (
          <Button>
            <div className="flex flex-row gap-2 items-center">
              <CirclePlus size={24} />
              <span>Ajouter une bannière</span>
            </div>
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <Form
          form={form}
          onSubmit={handleSubmit}
          className="w-full flex flex-col space-y-4"
        >
          <DialogHeader />
          <DialogTitle className="text-center">
            {isEdit ? 'Modifier' : 'Ajouter'} une bannière
          </DialogTitle>
          <DialogDescription className="text-center">
            {isEdit
              ? 'Modifier la bannière promotionnelle'
              : 'Ajouter une bannière promotionnelle'}
          </DialogDescription>

          <FormField
            control={form.control}
            name="region"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Région</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Choisissez une région" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="FR">
                      🇫🇷 Mademoiselle Culotte (FR)
                    </SelectItem>
                    <SelectItem value="US">🇺🇸 Orpair (US)</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="textParts"
            render={({ field }) => (
              <FormItem>
                <TextPartsEditor
                  value={field.value}
                  onChange={field.onChange}
                />
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="buttonText"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Texte du bouton</FormLabel>
                <FormControl>
                  <Input type="text" placeholder="Ex: Découvrir" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="buttonLink"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Lien du bouton</FormLabel>
                <FormControl>
                  <Input type="text" placeholder="https://..." {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="file"
            render={({ field: { value, onChange, ...fieldProps } }) => (
              <FormItem>
                <FormLabel>Image</FormLabel>
                {isEdit && (
                  <FormDescription>
                    Laisser vide pour conserver l'image actuelle
                  </FormDescription>
                )}
                <FormControl>
                  <Input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    {...fieldProps}
                    onChange={(e) => onChange(e.target.files?.[0])}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <DialogFooter className="flex gap-2">
            <DialogClose asChild>
              <Button variant="ghost">Annuler</Button>
            </DialogClose>
            <Button type="submit">{isEdit ? 'Modifier' : 'Ajouter'}</Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
